import {
  GCPImageSelectionStep,
  GCPImportStep,
  GCPSummaryStep,
  GCPTaggingStep,
  GCPWorkflowModeStep,
} from '@/components/features/gcpTagging'
import {
  TaskDetailsData,
  TaskDetailsStep,
  taskDetailsSchema,
} from '@/components/features/taskCreate/TaskDetailsStep'
import { TaskSettingsStep } from '@/components/features/taskCreate/TaskSettingsStep'
import { TaskSummaryStep } from '@/components/features/taskCreate/TaskSummaryStep'
import { UploadAssetSetStep } from '@/components/features/taskCreate/UploadAssetSetStep'
import {
  StepItem,
  WizardStepper,
} from '@/components/features/taskCreate/WizardStepper'
import { SelectedTaskTypeBar } from '@/components/features/taskCreate/taskTypes'
import { useClient } from '@/context/ClientContext'
import { fetchAssetSets } from '@/lib/clientHelpers'
import {
  computeLocalImageStats,
  toTaskInputImageStats,
} from '@/lib/localImageStats'
import { summaryKeys } from '@/lib/queryKeys'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'

import {
  AssetSet,
  AssetWithPresignedUrl,
  CreateAssetInput,
  CreateAssetSetInput,
  PresignedUrlType,
} from '@droidmap/asset-service-contract'
import { HeroPanel } from '@droidmap/frontend-ui'
import {
  CreatePhotogrammetryTaskInput,
  GCPData,
  GCPWorkflowMode,
  TaskInputImageStats,
  TaskType,
} from '@droidmap/project-service-contract'
import { DroidMapOdmSettings, OdmSettings } from '@droidmap/shared-odm-settings'
import { AssetSetType, AssetType } from '@droidmap/shared-service-contract'
import { isImageAsset } from '@droidmap/shared-service-utils'
import { sameOverlapVerdict } from '@droidmap/shared-utils'

export const Route = createFileRoute(
  '/_authenticated/orgs/$orgId_/projects/$projectId_/_pathless/tasks/create',
)({
  component: CreateTask,
  validateSearch: (
    search: Record<string, unknown>,
  ): { type?: 'photogrammetry' | 'gcp' } => {
    const type = search.type
    return {
      type: type === 'photogrammetry' || type === 'gcp' ? type : undefined,
    }
  },
  loader: async ({ context }) => {
    return { crumb: 'Create Task', metadata: context.metadata }
  },
})

/** Maps the `?type=` search param to the wizard's TaskType. */
const taskTypeFromSearch = (
  type: 'photogrammetry' | 'gcp' | undefined,
): TaskType | undefined => {
  if (type === 'photogrammetry') return TaskType.Photogrammetry
  if (type === 'gcp') return TaskType.GCPTagging
  return undefined
}

/** Maps a TaskType back to its `?type=` search param value. */
const searchFromTaskType = (type: TaskType): 'photogrammetry' | 'gcp' =>
  type === TaskType.GCPTagging ? 'gcp' : 'photogrammetry'

/* --------------------------- Wizard Step Types --------------------------- */

enum WizardStep {
  // Photogrammetry branch
  PhotogrammetryDetails = 'photogrammetry_details',
  PhotogrammetryOutputs = 'photogrammetry_outputs',
  PhotogrammetrySettings = 'photogrammetry_settings',
  PhotogrammetrySummary = 'photogrammetry_summary',
  PhotogrammetryUpload = 'photogrammetry_upload',

  // GCP Tagging branch (placeholder steps for now)
  GCPWorkflowMode = 'gcp_workflow_mode',
  GCPImageSelection = 'gcp_image_selection',
  GCPImport = 'gcp_import',
  GCPTagging = 'gcp_tagging',
  GCPSummary = 'gcp_summary',
}

/** First workflow step for a given task type. */
const firstStepForType = (type: TaskType): WizardStep =>
  type === TaskType.Photogrammetry
    ? WizardStep.PhotogrammetryDetails
    : WizardStep.GCPWorkflowMode

/**
 * Derive the progress-stepper model (visible steps + active index) for the
 * current branch. The step list is branch-aware: the Photogrammetry Upload
 * step only exists for new asset sets, and the GCP data step is labelled
 * Import or Tag depending on the workflow mode.
 */
const getStepModel = (
  selectedTaskType: TaskType,
  currentStep: WizardStep,
  inputType: TaskDetailsData['inputType'],
  gcpWorkflowMode: GCPWorkflowMode,
): { steps: StepItem[]; currentIndex: number } => {
  // Type selection lives in the SelectedTaskTypeBar above the stepper, so the
  // stepper only covers the post-selection workflow steps.
  if (selectedTaskType === TaskType.Photogrammetry) {
    const steps: StepItem[] = [
      { key: 'details', label: 'Details' },
      { key: 'output', label: 'Output' },
      { key: 'processing', label: 'Processing' },
      { key: 'summary', label: 'Summary' },
    ]
    if (inputType === 'new') {
      steps.push({ key: 'upload', label: 'Upload' })
    }
    const indexByStep: Partial<Record<WizardStep, number>> = {
      [WizardStep.PhotogrammetryDetails]: 0,
      [WizardStep.PhotogrammetryOutputs]: 1,
      [WizardStep.PhotogrammetrySettings]: 2,
      [WizardStep.PhotogrammetrySummary]: 3,
      [WizardStep.PhotogrammetryUpload]: 4,
    }
    return { steps, currentIndex: indexByStep[currentStep] ?? 0 }
  }

  if (selectedTaskType === TaskType.GCPTagging) {
    const isImport = gcpWorkflowMode === GCPWorkflowMode.IMPORT_EXISTING
    const steps: StepItem[] = [
      { key: 'mode', label: 'Mode' },
      { key: 'images', label: 'Images' },
      { key: 'data', label: isImport ? 'Import' : 'Tag' },
      { key: 'summary', label: 'Summary' },
    ]
    const indexByStep: Partial<Record<WizardStep, number>> = {
      [WizardStep.GCPWorkflowMode]: 0,
      [WizardStep.GCPImageSelection]: 1,
      [WizardStep.GCPImport]: 2,
      [WizardStep.GCPTagging]: 2,
      [WizardStep.GCPSummary]: 3,
    }
    return { steps, currentIndex: indexByStep[currentStep] ?? 0 }
  }

  return { steps: [], currentIndex: 0 }
}

/* --------------------------- Photogrammetry Data Types --------------------------- */

interface PhotogrammetryWizardData {
  taskDetails: TaskDetailsData
  droidMapOdmSettings?: DroidMapOdmSettings
  odmSettings?: OdmSettings
  assetSet?: AssetSet
  /**
   * Locally-measured stats for the new-upload flow's selected files (#2251),
   * sent on the create call so the quota gate can size (and 402) a job whose
   * just-created input set has no cached pixel total yet.
   */
  inputImageStats?: TaskInputImageStats
}

/* --------------------------- GCP Image Type --------------------------- */

interface GCPImageWithPosition {
  id: string
  name: string
  url: string
  thumbnailUrl?: string
  latitude?: number
  longitude?: number
  // Additional metadata for better GCP predictions
  relativeAltitude?: number
  absoluteAltitude?: number
  focalLength?: number
  focalLengthIn35mm?: number
  calibratedFocalLength?: number
  imageWidth?: number
  imageHeight?: number
  gimbalPitchDegree?: number
  gimbalYawDegree?: number
}

function convertAssetsToGCPImages(
  assets: AssetWithPresignedUrl[],
): GCPImageWithPosition[] {
  const imageAssets = assets.filter((asset) => isImageAsset(asset))
  const result: GCPImageWithPosition[] = []

  for (const asset of imageAssets) {
    if (!asset.presignedUrl) continue

    const position = asset.assetMetadata?.position
    const camera = asset.assetMetadata?.camera
    const drone = asset.assetMetadata?.drone
    const dimensions = asset.assetMetadata?.dimensions

    result.push({
      id: asset.id,
      name: asset.name,
      url: asset.presignedUrl,
      thumbnailUrl: asset.thumbnailPresignedUrl,
      latitude:
        typeof position?.latitude === 'number' ? position.latitude : undefined,
      longitude:
        typeof position?.longitude === 'number'
          ? position.longitude
          : undefined,
      relativeAltitude: position?.relativeAltitude,
      absoluteAltitude: position?.absoluteAltitude,
      focalLength: camera?.focalLength,
      focalLengthIn35mm: camera?.focalLengthIn35mm,
      calibratedFocalLength: camera?.calibratedFocalLength,
      imageWidth: dimensions?.width,
      imageHeight: dimensions?.height,
      gimbalPitchDegree: drone?.gimbalPitchDegree,
      gimbalYawDegree: drone?.gimbalYawDegree,
    })
  }

  return result
}

/* --------------------------- Create Task Wizard --------------------------- */

function CreateTaskWizard({
  existingInputAssetSets,
  projectName,
  initialTaskType,
  onCompletePhotogrammetry,
  onSaveGCPFile,
  onGCPComplete,
  isCreating = false,
  createError,
  onResetCreateError,
}: {
  existingInputAssetSets: AssetSet[]
  projectName: string
  initialTaskType?: TaskType
  /**
   * Fire-and-forget: a failure lands on {@link createError}, never as a
   * rejection — callers here don't await it, and an unawaited rejection is
   * how a 402 used to vanish (#2910).
   */
  onCompletePhotogrammetry: (data: PhotogrammetryWizardData) => void
  onSaveGCPFile: (file: File, filename: string) => Promise<void>
  onGCPComplete: () => void
  isCreating?: boolean
  /** Why the last photogrammetry create failed, if it did. */
  createError?: unknown
  /** Clear {@link createError}, so it doesn't follow the user to another step. */
  onResetCreateError: () => void
}) {
  const { orgId, projectId } = Route.useParams()
  const { metadata } = Route.useLoaderData()
  const { assetClient } = useClient()
  const navigate = useNavigate()

  // First-step Back exits the flow to the project page (the type-selection
  // screen is disabled, so there's no earlier step to return to).
  const goToProject = () =>
    navigate({ to: `/orgs/${orgId}/projects/${projectId}` })

  // Type is chosen up front via the New Task / Change dropdowns (`?type=`), so
  // the flow starts directly in that type's first step (defaulting to
  // Photogrammetry when no type is supplied).
  const defaultTaskType = initialTaskType ?? TaskType.Photogrammetry
  const [currentStep, setCurrentStep] = useState<WizardStep>(
    firstStepForType(defaultTaskType),
  )
  const [selectedTaskType, setSelectedTaskType] =
    useState<TaskType>(defaultTaskType)

  // Photogrammetry-specific state
  const [taskDetails, setTaskDetails] = useState<TaskDetailsData>({
    taskName: '',
    inputType: 'new',
    assetSetType: AssetSetType.DroneImages,
    newAssetSetName: '',
  })
  const [droidMapOdmSettings, setDroidMapOdmSettings] =
    useState<DroidMapOdmSettings>()
  const [odmSettings, setOdmSettings] = useState<OdmSettings>()
  // The new-upload flow's input set, once uploaded. Kept so a create that
  // fails after the upload (a 402 once the imagery is sized, or anything
  // else) is retried against it rather than paying for the upload twice
  // (#2910).
  const [uploadedAssetSet, setUploadedAssetSet] = useState<AssetSet>()
  // The overlap verdict the user has accepted (droidmap#3019). Compared by
  // content, not identity: going back to the details step recomputes the
  // verdict for the same files, and that must not re-warn — while a changed
  // selection brings a different verdict that has to be accepted afresh.
  const [acknowledgedOverlap, setAcknowledgedOverlap] =
    useState<TaskDetailsData['overlapVerdict']>()

  // Locally measure the selected files for the new-upload flow's pre-flight
  // cost estimate + quota gate (#2251). Lives at the wizard level so the
  // summary step's estimate and the final create call share one measurement.
  // Keyed on the file identities so re-entering the summary step reuses it.
  const statsFiles =
    taskDetails.inputType === 'new' ? (taskDetails.files ?? []) : []
  const localImageStatsQuery = useQuery({
    queryKey: [
      'localImageStats',
      statsFiles
        .map((file) => `${file.name}:${file.size}:${file.lastModified}`)
        .join('|'),
    ],
    queryFn: () => computeLocalImageStats(statsFiles),
    enabled: statsFiles.length > 0,
    staleTime: Infinity,
  })
  const inputImageStats = toTaskInputImageStats(localImageStatsQuery.data)

  // GCP Tagging-specific state
  const [gcpTaskName, setGcpTaskName] = useState('')
  const [gcpWorkflowMode, setGcpWorkflowMode] = useState<GCPWorkflowMode>(
    GCPWorkflowMode.CREATE_NEW,
  )
  const [gcpInputAssetSet, setGcpInputAssetSet] = useState<AssetSet>()
  const [gcpData, setGcpData] = useState<GCPData>()
  const [gcpImages, setGcpImages] = useState<GCPImageWithPosition[]>([])
  const [isLoadingGcpImages, setIsLoadingGcpImages] = useState(false)

  /* ----------------------- Load GCP Images from Asset Set ----------------------- */

  useEffect(() => {
    const loadGcpImages = async () => {
      // Only load when we have an asset set and we're on or heading to the tagging step
      if (!gcpInputAssetSet || currentStep !== WizardStep.GCPTagging) {
        return
      }

      setIsLoadingGcpImages(true)
      try {
        // Fetch all pages of assets with presigned URLs
        let next: string | undefined = undefined
        const allAssets: AssetWithPresignedUrl[] = []

        do {
          const assetsResult = await assetClient.listAssetsByAssetSet(
            {
              assetSetId: gcpInputAssetSet.id,
              presignedUrlType: PresignedUrlType.GET,
              next,
            },
            metadata,
          )

          if (assetsResult.items) {
            allAssets.push(...assetsResult.items)
          }

          next = assetsResult.next
        } while (next)

        // Convert to GCP image format
        const images = convertAssetsToGCPImages(allAssets)
        setGcpImages(images)
      } catch (error) {
        console.error('Error loading GCP images:', error)
        setGcpImages([])
      } finally {
        setIsLoadingGcpImages(false)
      }
    }

    loadGcpImages()
  }, [gcpInputAssetSet, currentStep, assetClient, metadata])

  /* ----------------------- Task Type Selection Handlers ----------------------- */

  const handleTaskTypeSelect = (taskType: TaskType) => {
    setSelectedTaskType(taskType)
    // Selection is the entry into the flow — no separate Continue step.
    setCurrentStep(firstStepForType(taskType))
    // Keep the URL in sync so refresh / deep-linking reflects the choice.
    navigate({
      to: '.',
      search: (prev) => ({ ...prev, type: searchFromTaskType(taskType) }),
      replace: true,
    })
  }

  /* ----------------------- Photogrammetry Handlers ----------------------- */

  const handlePhotogrammetryDetailsComplete = (data: TaskDetailsData) => {
    // An uploaded set only stands in for the input it was uploaded from.
    const sameUploadInput =
      data.inputType === 'new' &&
      data.assetSetType === taskDetails.assetSetType &&
      data.newAssetSetName === taskDetails.newAssetSetName &&
      data.files?.length === taskDetails.files?.length &&
      (data.files ?? []).every((file, i) => file === taskDetails.files?.[i])
    if (!sameUploadInput) setUploadedAssetSet(undefined)
    setTaskDetails(data)
    setCurrentStep(WizardStep.PhotogrammetryOutputs)
  }

  const handlePhotogrammetrySettingsComplete = (
    nextDroidMapOdmSettings: DroidMapOdmSettings,
    nextOdmSettings: OdmSettings,
  ) => {
    setDroidMapOdmSettings(nextDroidMapOdmSettings)
    setOdmSettings(nextOdmSettings)
    setCurrentStep(WizardStep.PhotogrammetrySummary)
  }

  const createWithUploadedAssetSet = (assetSet: AssetSet) =>
    onCompletePhotogrammetry({
      taskDetails,
      droidMapOdmSettings,
      odmSettings,
      assetSet,
      inputImageStats,
    })

  const handlePhotogrammetrySummaryComplete = () => {
    if (taskDetails.inputType === 'new' && uploadedAssetSet) {
      // Back here after a create failed post-upload: going to the upload
      // step would remount the uploader and send every image again.
      createWithUploadedAssetSet(uploadedAssetSet)
    } else if (taskDetails.inputType === 'new') {
      setCurrentStep(WizardStep.PhotogrammetryUpload)
    } else {
      onCompletePhotogrammetry({
        taskDetails,
        droidMapOdmSettings,
        odmSettings,
      })
    }
  }

  const handlePhotogrammetryAssetSetComplete = (assetSet: AssetSet) => {
    if (!taskDetails) {
      console.error('Task details or settings are not set')
      return
    }
    setUploadedAssetSet(assetSet)
    createWithUploadedAssetSet(assetSet)
  }

  /* ----------------------- GCP Tagging Handlers ----------------------- */

  const handleSaveGCPToProject = async (file: File, filename: string) => {
    await onSaveGCPFile(file, filename)
  }

  const handleGCPComplete = () => {
    onGCPComplete()
  }

  /* ----------------------- Render ----------------------- */

  const { steps, currentIndex } = getStepModel(
    selectedTaskType,
    currentStep,
    taskDetails.inputType,
    gcpWorkflowMode,
  )

  const stepContent = (
    <>
      {/* Photogrammetry Flow */}
      {currentStep === WizardStep.PhotogrammetryDetails && (
        <TaskDetailsStep
          onNext={handlePhotogrammetryDetailsComplete}
          initialData={taskDetails}
          existingInputAssetSets={existingInputAssetSets}
          orgId={orgId}
          projectId={projectId}
          metadata={metadata}
        />
      )}
      {(currentStep === WizardStep.PhotogrammetryOutputs ||
        currentStep === WizardStep.PhotogrammetrySettings) && (
        <TaskSettingsStep
          view={
            currentStep === WizardStep.PhotogrammetryOutputs
              ? 'outputs'
              : 'processing'
          }
          onContinue={() => setCurrentStep(WizardStep.PhotogrammetrySettings)}
          onNext={handlePhotogrammetrySettingsComplete}
          onBack={() =>
            setCurrentStep(
              currentStep === WizardStep.PhotogrammetrySettings
                ? WizardStep.PhotogrammetryOutputs
                : WizardStep.PhotogrammetryDetails,
            )
          }
          initialData={droidMapOdmSettings}
          boundaryAssetName={taskDetails.boundaryAssetName}
          gcpAssetName={taskDetails.gcpAssetName}
          orgId={orgId}
          projectId={projectId}
        />
      )}
      {currentStep === WizardStep.PhotogrammetrySummary && (
        <TaskSummaryStep
          onNext={handlePhotogrammetrySummaryComplete}
          onBack={() => {
            onResetCreateError()
            setCurrentStep(WizardStep.PhotogrammetrySettings)
          }}
          taskDetails={taskDetails}
          droidMapOdmSettings={droidMapOdmSettings}
          odmSettings={odmSettings}
          isCreating={isCreating}
          orgId={orgId}
          projectId={projectId}
          metadata={metadata}
          inputImageStats={inputImageStats}
          inputImageStatsPending={localImageStatsQuery.isLoading}
          createError={createError}
          inputAlreadyUploaded={
            taskDetails.inputType === 'new' && !!uploadedAssetSet
          }
          overlapAcknowledged={
            !!taskDetails.overlapVerdict &&
            sameOverlapVerdict(acknowledgedOverlap, taskDetails.overlapVerdict)
          }
          onOverlapAcknowledged={() =>
            setAcknowledgedOverlap(taskDetails.overlapVerdict)
          }
        />
      )}
      {currentStep === WizardStep.PhotogrammetryUpload && (
        <UploadAssetSetStep
          onComplete={handlePhotogrammetryAssetSetComplete}
          onBack={() => {
            onResetCreateError()
            setCurrentStep(WizardStep.PhotogrammetrySummary)
          }}
          onRetryCreate={() => {
            if (uploadedAssetSet) createWithUploadedAssetSet(uploadedAssetSet)
          }}
          taskDetails={taskDetails}
          orgId={orgId}
          projectId={projectId}
          metadata={metadata}
          uploadedAssetSet={uploadedAssetSet}
          isCreating={isCreating}
          createError={createError}
        />
      )}

      {/* GCP Tagging Flow */}
      {currentStep === WizardStep.GCPWorkflowMode && (
        <GCPWorkflowModeStep
          onNext={(taskName, mode) => {
            setGcpTaskName(taskName)
            setGcpWorkflowMode(mode)
            setCurrentStep(WizardStep.GCPImageSelection)
          }}
          onBack={goToProject}
          initialTaskName={gcpTaskName}
          initialWorkflowMode={gcpWorkflowMode}
        />
      )}

      {currentStep === WizardStep.GCPImageSelection && (
        <GCPImageSelectionStep
          onNext={(assetSet) => {
            setGcpInputAssetSet(assetSet)
            if (gcpWorkflowMode === GCPWorkflowMode.IMPORT_EXISTING) {
              setCurrentStep(WizardStep.GCPImport)
            } else {
              setCurrentStep(WizardStep.GCPTagging)
            }
          }}
          onBack={() => setCurrentStep(WizardStep.GCPWorkflowMode)}
          existingAssetSets={existingInputAssetSets}
          orgId={orgId}
          projectId={projectId}
          metadata={metadata}
          initialAssetSet={gcpInputAssetSet}
        />
      )}

      {currentStep === WizardStep.GCPImport && (
        <GCPImportStep
          onNext={(data) => {
            setGcpData(data)
            setCurrentStep(WizardStep.GCPSummary)
          }}
          onBack={() => setCurrentStep(WizardStep.GCPImageSelection)}
        />
      )}

      {currentStep === WizardStep.GCPTagging &&
        (isLoadingGcpImages ? (
          <div className="flex items-center justify-center h-64">
            <p className="text-text/75">Loading images...</p>
          </div>
        ) : (
          <GCPTaggingStep
            onNext={(data) => {
              setGcpData(data)
              setCurrentStep(WizardStep.GCPSummary)
            }}
            onBack={() => setCurrentStep(WizardStep.GCPImageSelection)}
            images={gcpImages}
            initialData={gcpData}
            predictionsEnabled={false}
          />
        ))}

      {currentStep === WizardStep.GCPSummary && (
        <GCPSummaryStep
          onNext={handleGCPComplete}
          onBack={() =>
            setCurrentStep(
              gcpWorkflowMode === GCPWorkflowMode.IMPORT_EXISTING
                ? WizardStep.GCPImport
                : WizardStep.GCPTagging,
            )
          }
          onSaveToProject={handleSaveGCPToProject}
          taskName={gcpTaskName}
          workflowMode={gcpWorkflowMode}
          inputAssetSet={gcpInputAssetSet}
          gcpData={gcpData}
          isCreating={isCreating}
        />
      )}
    </>
  )

  return (
    <div className="space-y-6">
      <HeroPanel title="Create Task" description={`Project: ${projectName}`} />

      <SelectedTaskTypeBar
        selectedType={selectedTaskType}
        onSelect={handleTaskTypeSelect}
      />

      {steps.length > 1 && (
        <WizardStepper
          steps={steps}
          currentIndex={currentIndex}
          className="px-6"
        />
      )}
      {stepContent}
    </div>
  )
}

/* --------------------------- Create Task Page --------------------------- */

function CreateTask() {
  const queryClient = useQueryClient()
  const { projectClient, assetClient } = useClient()
  const navigate = useNavigate()
  const { orgId, projectId } = Route.useParams()
  const { metadata } = Route.useLoaderData()
  const { type } = Route.useSearch()

  // Both completion paths land on the Tasks tab, where the new task is the
  // top row (`created desc`). Invalidating covers every page/sort variant of
  // the paginated list, so the row is there on arrival rather than a poll
  // later. Switch to the task-detail route once #1044 lands.
  const goToTaskList = () => {
    void queryClient.invalidateQueries({ queryKey: ['tasks', projectId] })
    void queryClient.invalidateQueries({
      queryKey: summaryKeys.tasks(projectId),
    })
    navigate({
      to: '/orgs/$orgId/projects/$projectId/tasks',
      params: { orgId, projectId },
    })
  }

  const {
    data: project,
    isError: isProjectError,
    error: projectError,
    isLoading: isLoadingProject,
  } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => projectClient.getProject({ projectId }, metadata),
  })

  const assetSetsQuery = useQuery({
    queryKey: ['assetSets', projectId],
    queryFn: () => {
      if (!project) {
        return Promise.reject('Project data is not available yet.')
      }
      return fetchAssetSets([project], assetClient, metadata)
    },
    enabled: !!project,
  })

  /* ----------------------- Photogrammetry Mutation ----------------------- */

  const createPhotogrammetryMutation = useMutation({
    mutationFn: async (formData: PhotogrammetryWizardData) => {
      if (!project?.id) {
        throw new Error('Project ID is not defined')
      }

      if (!project?.orgId) {
        throw new Error('Org ID is not defined')
      }

      if (!metadata.projectId) {
        throw new Error('Metadata is not defined')
      }

      const validSchema = taskDetailsSchema.safeParse(formData.taskDetails)

      if (!validSchema.success) {
        throw new Error(
          validSchema.error.issues.map((issue) => issue.message).join(', '),
        )
      }

      const inputAssetSets: AssetSet[] = []
      if (formData.taskDetails.inputType === 'existing') {
        if (!formData.taskDetails.existingAssetSet) {
          throw new Error('Existing asset set is not set')
        }
        inputAssetSets.push(formData.taskDetails.existingAssetSet)
      } else {
        if (!formData.assetSet) {
          throw new Error('Asset set is not set')
        }
        inputAssetSets.push(formData.assetSet)
      }

      let odmSettings = formData.odmSettings

      // Set boundary asset name in ODM settings if provided
      if (
        formData.taskDetails.boundaryAssetName &&
        odmSettings?.stageOptions?.general
      ) {
        odmSettings = {
          ...odmSettings,
          stageOptions: {
            ...odmSettings.stageOptions,
            general: {
              ...odmSettings.stageOptions.general,
              boundary: formData.taskDetails.boundaryAssetName,
            },
          },
        }
      }

      // Set GCP asset name in ODM settings if provided
      if (
        formData.taskDetails.gcpAssetName &&
        odmSettings?.stageOptions?.general
      ) {
        odmSettings = {
          ...odmSettings,
          stageOptions: {
            ...odmSettings.stageOptions,
            general: {
              ...odmSettings.stageOptions.general,
              gcp: formData.taskDetails.gcpAssetName,
            },
          },
        }
      }

      const photogrammetryTaskInput: CreatePhotogrammetryTaskInput = {
        projectId: project.id,
        name: formData.taskDetails.taskName,
        type: TaskType.Photogrammetry,
        inputAssetSetReferences: inputAssetSets.map((assetSet) => ({
          id: assetSet.id,
          name: assetSet.name,
          assetSetType: assetSet.assetSetType,
        })),
        odmSettings: odmSettings,
        boundaryAssetId: formData.taskDetails.boundaryAssetId,
        gcpAssetId: formData.taskDetails.gcpAssetId,
        // No `computeResourcesOverride`: on-demand is the platform default,
        // decided in the pipeline Lambdas rather than by any one client.
        // `useOnDemand: false` is the explicit spot opt-in, and the wizard
        // deliberately doesn't offer it — see #2588.
        // Admission-only sizing for the quota gate — the freshly-uploaded
        // input set has no cached pixel total yet (#2251).
        inputImageStats: formData.inputImageStats,
      }

      return await projectClient.createTask(photogrammetryTaskInput, metadata)
    },
    onSuccess: () => {
      goToTaskList()
    },
    // The step shows the user a banner; this is the trace. A client-side
    // throw above (schema, missing set) reads there as a retryable failure
    // that never succeeds, so it has to be findable somewhere (#2910).
    onError: (error) => {
      console.error('Task creation failed', error)
    },
  })

  /* ----------------------- GCP File Upload Mutation ----------------------- */

  const uploadGCPFileMutation = useMutation({
    mutationFn: async ({
      file,
      filename,
    }: {
      file: File
      filename: string
    }) => {
      if (!project?.id) {
        throw new Error('Project ID is not defined')
      }

      if (!project?.orgId) {
        throw new Error('Org ID is not defined')
      }

      // Create an asset set for GCP outputs. This set is populated by an
      // upload too — one small text file — so it declares its size like
      // any other upload path (#2294), leaving the job's *output* sets as
      // the only undeclared creations.
      const assetSetInput: CreateAssetSetInput = {
        projectId: project.id,
        name: `GCP File - ${filename.replace(/_gcp_list\.txt$/, '')}`,
        assetSetType: AssetSetType.GCPOutputs,
        declaredTotalBytes: file.size,
      }

      const assetSet = await assetClient.createAssetSet(assetSetInput, metadata)

      // Create an asset for the GCP file with a presigned upload URL
      const assetInput: CreateAssetInput = {
        assetSetId: assetSet.id,
        name: filename,
        assetType: AssetType.Txt,
        presignedUrlType: PresignedUrlType.PUT,
      }

      const asset = await assetClient.createAsset(assetInput, metadata)

      if (!asset.presignedUrl) {
        throw new Error('Failed to get presigned upload URL')
      }

      // Upload the file to S3
      const uploadResponse = await fetch(asset.presignedUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': 'text/plain',
        },
        body: file,
      })

      if (!uploadResponse.ok) {
        throw new Error(`Failed to upload file: ${uploadResponse.statusText}`)
      }

      return { assetSet, asset }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['assetSets', projectId] })
      void queryClient.invalidateQueries({
        queryKey: summaryKeys.assetSets(projectId),
      })
    },
  })

  const handleGCPComplete = () => {
    goToTaskList()
  }

  if (isLoadingProject) {
    return <div>Loading...</div>
  }

  if (isProjectError) {
    return <div>Error: {projectError?.message}</div>
  }

  if (!project) {
    return <div>Project not found</div>
  }

  return (
    <div>
      <CreateTaskWizard
        existingInputAssetSets={
          assetSetsQuery.data?.filter(
            (set) => set.assetSetType === AssetSetType.DroneImages,
          ) || []
        }
        projectName={project.name}
        initialTaskType={taskTypeFromSearch(type)}
        // `mutate`, not `mutateAsync`: the failure is rendered from the
        // mutation's `error` on the step that sent it, so there is no
        // rejection to leave unhandled (#2910).
        onCompletePhotogrammetry={(data) =>
          createPhotogrammetryMutation.mutate(data)
        }
        onSaveGCPFile={async (file, filename) => {
          await uploadGCPFileMutation.mutateAsync({ file, filename })
        }}
        onGCPComplete={handleGCPComplete}
        isCreating={
          createPhotogrammetryMutation.isPending ||
          uploadGCPFileMutation.isPending
        }
        createError={createPhotogrammetryMutation.error ?? undefined}
        onResetCreateError={createPhotogrammetryMutation.reset}
      />
    </div>
  )
}
